import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { SpecificationStreamContent, SpecificationStreamProvider } from '../../types/specification';
import type { SpecificationConfirmClaimsRepository } from '../../services/specification-confirm-claims-repository';
import { SpecificationOperateType } from '../../enums';
import type { SpecificationConfirmClaimItem, SpecificationConfirmClaimsPayload, SpecificationConfirmClaimTree } from '../../types/specification-confirm-claims';
import { FormatClaimApi } from '../../apis/format-claim';
import { GenFeatureTreeApi, type FeatureTree, type GenFeatureTreeResponse } from '../../apis/gen-feature-tree';
import { SubmitConfirmClaimsContentApi, type SubmitConfirmClaimsContentFigure, type SubmitConfirmClaimsContentTerm } from '../../apis/submit-confirm-claims-content';
import { genClaimId, genFeatureId, http2StreamProvider, SSE2StreamProvider } from '../../utils';
import _ from 'lodash';
import type { Figure, Term } from '../../services/impls/specification';

@injectable()
export class SpecificationConfirmClaimsRepositoryImpl implements SpecificationConfirmClaimsRepository {
    constructor(
        @inject(FormatClaimApi)
        private _formatClaimApi: FormatClaimApi,
        @inject(GenFeatureTreeApi)
        private _genFeatureTreeApi: GenFeatureTreeApi,
        @inject(SubmitConfirmClaimsContentApi)
        private _submitConfirmClaimsContentApi: SubmitConfirmClaimsContentApi,
    ) {}

    genFormattedClaim(taskId: string): SpecificationStreamProvider<SpecificationStreamContent<string>> {
        return http2StreamProvider((options) => {
            return this._formatClaimApi.formatClaim({
                task_id: taskId,
            }, options);
        }, data => data?.formatted_claim || '');
    }

    private _featureTrue2claimTree(response?: GenFeatureTreeResponse): SpecificationConfirmClaimTree {
        // 创建映射表用于快速查找节点
        const nodeMap = new Map<string, SpecificationConfirmClaimItem>();
        const roots: SpecificationConfirmClaimItem[] = [];

        // 第一次遍历：创建所有节点并存入映射表
        response?.feature_tree?.forEach(item => {
            const newNode: SpecificationConfirmClaimItem = {
                id: genClaimId(),
                num: item.num,
                simpleClaim: item.simple_claim,
                claimFeatures: item.claim_features?.length
                    ? item.claim_features.map(feature => ({
                        ...feature,
                        id: genFeatureId(),
                        concepts: feature.concepts || [],
                    }))
                    : [{
                        id: genFeatureId(),
                        feature: '',
                        concepts: [],
                    }],
            };
            nodeMap.set(item.num, newNode);
        });

        // 第二次遍历：建立父子关系
        response?.feature_tree?.forEach(item => {
            const currentNode = nodeMap.get(item.num)!;
            // !!! 从权的父权可能有多个，暂时只取最后一个作为父级
            const parent = _.last(item.parent?.split(','));
            if (parent) {
                // 找到父节点并添加当前节点到其children
                const parentNode = nodeMap.get(parent);
                if (parentNode) {
                    parentNode.children = parentNode.children || [];
                    parentNode.children.push(currentNode);
                }
            } else {
                // 没有父节点时作为根节点
                roots.push(currentNode);
            }
        });

        return roots;
    }

    private _claimTree2featureTree(tree: SpecificationConfirmClaimTree): FeatureTree {
        const feature_tree: FeatureTree = [];

        // 递归处理树节点
        function processNode(
            node: SpecificationConfirmClaimItem,
            parentNum: string = '',
        ) {
            // 添加当前节点
            feature_tree.push({
                num: node.num,
                simple_claim: node.simpleClaim,
                claim_features: node.claimFeatures.map(feature => ({
                    feature: feature.feature,
                    concepts: feature.concepts,
                })),
                parent: parentNum,
            });

            // 递归处理子节点
            node.children?.forEach(child => {
                processNode(child, node.num);
            });
        }

        // 处理所有根节点
        tree.forEach(rootNode => {
            processNode(rootNode);
        });

        return feature_tree;
    }

    genClaimTree(taskId: string, regenerate?: boolean): SpecificationStreamProvider<SpecificationStreamContent<SpecificationConfirmClaimTree>> {
        const sse = this._genFeatureTreeApi.genFeatureTree({
            task_id: taskId,
            operate_type: regenerate ? SpecificationOperateType.Regenerate : undefined,
        });
        return SSE2StreamProvider(sse, this._featureTrue2claimTree);
    }

    private _figures2figures(figures: Figure[]): SubmitConfirmClaimsContentFigure[] {
        return _.map(figures, figure => {
            const annotations = _.map(figure.annotations, annotation => ({
                term_unique_id: annotation.getTermId(),
                properties: {
                    type: annotation.type,
                    attrs: annotation.attrs,
                },
            }));
            return {
                unique_id: figure.getId(),
                image: {
                    s3_key: figure.getS3Key(),
                    properties: {
                        scale: figure.scale,
                        rotation: figure.rotation,
                        x: figure.x,
                        y: figure.y,
                    },
                },
                abstract_figure: figure.isAbstract,
                annotation_image_base64: figure.imageWithAnnotations,
                annotations,
            };
        });
    }

    private _terms2terms(terms: Term[]): SubmitConfirmClaimsContentTerm[] {
        return _.map(terms, term => ({
            unique_id: term.getId(),
            number: term.getNumber(),
            name: term.text,
        }));
    }

    async submit(taskId: string, payload: SpecificationConfirmClaimsPayload): Promise<void> {
        const { specification } = payload;
        const figures = specification.getAllFigures();
        const terms = specification.getAllTerms();

        await this._submitConfirmClaimsContentApi.submitConfirmClaimsContent({
            task_id: taskId,
            feature_tree: this._claimTree2featureTree(payload.claimTree),
            figures: this._figures2figures(figures),
            terms: this._terms2terms(terms),
        });
    }
}
